import axios, { AxiosRequestConfig } from "axios";
import { getAuth } from "../../auth";
import { fetchListOrEmpty, extractList } from "../../estoque/utils/estoque.load";
import { parseCurrency } from "./cardapio.utils";

const apiUrl = () => process.env.REACT_APP_API_URL || "";

export interface CardapioIngrediente {
  _id_stock_item: number;
  stock_item_name: string;
  quantity: number;
  unit: string;
}

export interface CardapioItem {
  _id: number;
  name: string;
  serves_people: number;
  preparation_time_minutes?: number;
  price: number;
  _id_sector?: number;
  sector_name?: string;
  current_quantity?: number;
  minimum_quantity?: number;
  item_type?: string;
  technical_sheet?: string;
  photo?: string;
  allergens?: string[];
  ingredients?: CardapioIngrediente[];
}

export interface PaginatedCardapioResponse {
  page: number;
  amount: number;
  total: number;
  itens: CardapioItem[];
  data?: CardapioItem[];
}

export interface CardapioPedidoItem {
  _id_cardapio_item: number;
  item_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export interface CardapioPedido {
  _id: number;
  _id_usuario?: number | null;
  waiter_name: string;
  wristband_number: string;
  seller_name?: string;
  commission_percent?: number;
  commission_value?: number;
  notes?: string;
  total: number;
  status?: string;
  created_at?: string;
  items: CardapioPedidoItem[];
}

export const CARDAPIO_PEDIDO_STATUS = {
  EM_ABERTO: "Em aberto",
  PAGO: "Pago",
} as const;

export interface CardapioReportSummary {
  orders: number;
  revenue: number;
  commission: number;
}

export interface CardapioReportProductRow {
  _id_cardapio_item: number;
  item_name: string;
  quantity: number;
  revenue: number;
}

export interface CardapioReportSellerRow {
  seller_name: string;
  orders: number;
  revenue: number;
  commission: number;
}

export interface CardapioOperationalReport {
  summary: CardapioReportSummary;
  by_product: CardapioReportProductRow[];
  by_seller: CardapioReportSellerRow[];
}

function headers(operation: string) {
  const auth = getAuth();
  return {
    Authorization: `Bearer ${auth?.api_token}`,
    operation,
  };
}

function client(operation: string): AxiosRequestConfig {
  return { headers: headers(operation) };
}

function normalizeIngrediente(raw: unknown): CardapioIngrediente {
  const row = raw as Record<string, unknown>;
  return {
    _id_stock_item: Number(row._id_stock_item ?? row._id_item_estoque ?? 0),
    stock_item_name: String(row.stock_item_name ?? row.nome_item ?? ""),
    quantity: Number(row.quantity ?? row.quantidade ?? 0),
    unit: String(row.unit ?? row.unidade ?? ""),
  };
}

function unwrapCardapioItemRow(raw: unknown): Record<string, unknown> {
  const row = (raw as Record<string, unknown>) || {};
  if (row.cardapio_item && typeof row.cardapio_item === "object") {
    return row.cardapio_item as Record<string, unknown>;
  }
  if (row.item && typeof row.item === "object" && !Array.isArray(row.item)) {
    return row.item as Record<string, unknown>;
  }
  return row;
}

function readCardapioItemName(row: Record<string, unknown>): string {
  const candidates = [row.name, row.nome, row.nome_item, row.item_name, row.produto];
  for (const candidate of candidates) {
    const text = String(candidate ?? "").trim();
    if (text) return text;
  }
  return "";
}

function readCardapioItemPrice(row: Record<string, unknown>): number {
  const raw = row.price ?? row.valor ?? row.preco ?? row.valor_unitario ?? 0;
  return parseCurrency(raw as string | number);
}

export function normalizeCardapioItem(raw: unknown): CardapioItem {
  const row = unwrapCardapioItemRow(raw);
  const ingredientRows = Array.isArray(row.ingredients)
    ? row.ingredients
    : Array.isArray(row.ingredientes)
      ? row.ingredientes
      : [];
  const ingredients = ingredientRows.map(normalizeIngrediente);

  const sector = row.sector as Record<string, unknown> | null | undefined;
  const id = Number(row._id ?? row._id_cardapio_item ?? row.id ?? 0);
  const itemTypeRaw = String(
    row.item_type ??
      row.tipo_item ??
      row.tipo ??
      row.tipo_venda ??
      row.aluguel_venda ??
      "",
  ).trim();
  const currentQty = Number(
    row.current_quantity ?? row.quantidade_atual ?? row.stock_quantity,
  );
  const minimumQty = Number(
    row.minimum_quantity ?? row.quantidade_minima ?? row.stock_minimum,
  );

  return {
    _id: id,
    name: readCardapioItemName(row),
    serves_people: Number(row.serves_people ?? row.pessoas_servem ?? 0),
    preparation_time_minutes:
      Number(row.preparation_time_minutes ?? row.tempo_preparo_min ?? 0) ||
      undefined,
    price: readCardapioItemPrice(row),
    _id_sector: Number(row._id_sector ?? row._id_setor ?? 0) || undefined,
    sector_name: String(row.sector_name ?? row.setor_nome ?? sector?.name ?? "").trim() || undefined,
    current_quantity: Number.isFinite(currentQty) ? currentQty : undefined,
    minimum_quantity: Number.isFinite(minimumQty) ? minimumQty : undefined,
    item_type: itemTypeRaw || undefined,
    technical_sheet: (row.technical_sheet ?? row.ficha_tecnica) as string | undefined,
    photo: (row.photo ?? row.foto) as string | undefined,
    allergens: (row.allergens ?? row.alergenos ?? []) as string[],
    ingredients,
  };
}

export function normalizeCardapioPedido(raw: unknown): CardapioPedido {
  const row = (raw as Record<string, unknown>) || {};
  const items = Array.isArray(row.items)
    ? row.items.map((line) => {
        const r = line as Record<string, unknown>;
        return {
          _id_cardapio_item: Number(r._id_cardapio_item ?? 0),
          item_name: String(r.item_name ?? r.nome_item ?? ""),
          quantity: Number(r.quantity ?? r.quantidade ?? 0),
          unit_price: Number(r.unit_price ?? r.valor_unitario ?? 0),
          total_price: Number(r.total_price ?? r.valor_total ?? 0),
        };
      })
    : [];

  return {
    _id: Number(row._id ?? row._id_cardapio_pedido ?? 0),
    _id_usuario: (() => {
      const id = Number(row._id_usuario ?? row.cliente_id ?? 0);
      return id > 0 ? id : null;
    })(),
    waiter_name: String(row.waiter_name ?? row.garcom ?? ""),
    wristband_number: String(row.wristband_number ?? row.numero_pulseira ?? ""),
    seller_name:
      String(row.seller_name ?? row.vendedor ?? "").trim() || undefined,
    commission_percent:
      Number(row.commission_percent ?? row.percentual_comissao ?? 0) ||
      undefined,
    commission_value:
      Number(row.commission_value ?? row.valor_comissao ?? 0) || undefined,
    notes: (row.notes ?? row.observacao) as string | undefined,
    total: Number(row.total ?? 0),
    status:
      String(row.status ?? "").trim() || CARDAPIO_PEDIDO_STATUS.EM_ABERTO,
    created_at: String(row.created_at ?? row.criado_em ?? "").trim() || undefined,
    items,
  };
}

export async function fetchCardapioItens(
  search?: string,
  _id_sector?: number,
): Promise<CardapioItem[]> {
  return fetchListOrEmpty(async () => {
    const response = await axios.get<PaginatedCardapioResponse>(`${apiUrl()}/cardapio`, {
      ...client("cardapio/find"),
      params: {
        page: 1,
        amount: 50,
        search,
        ...(_id_sector ? { _id_sector } : {}),
      },
    });
    return extractList(response.data)
      .map(normalizeCardapioItem)
      .filter((item) => item._id > 0);
  });
}

export async function fetchCardapioItemById(_id: number): Promise<CardapioItem> {
  const response = await axios.get(`${apiUrl()}/cardapio/${_id}`, client("cardapio/find-by-id"));
  return normalizeCardapioItem(response.data);
}

export async function createCardapioItem(data: Record<string, unknown>) {
  const response = await axios.post(`${apiUrl()}/cardapio`, data, client("cardapio/create"));
  return normalizeCardapioItem(response.data);
}

export async function updateCardapioItem(_id: number, data: Record<string, unknown>) {
  const response = await axios.put(`${apiUrl()}/cardapio/${_id}`, data, client("cardapio/update"));
  return normalizeCardapioItem(response.data);
}

export async function deleteCardapioItem(_id: number) {
  await axios.delete(`${apiUrl()}/cardapio/${_id}`, client("cardapio/delete"));
}

export async function createCardapioPedido(
  data: Record<string, unknown>,
): Promise<CardapioPedido> {
  const response = await axios.post(
    `${apiUrl()}/cardapio/pedidos`,
    data,
    client("cardapio/pedidos/create"),
  );
  return normalizeCardapioPedido(response.data);
}

export async function appendCardapioPedidoItems(
  _id: number,
  items: Array<{ _id_cardapio_item: number; quantity: number }>,
): Promise<CardapioPedido> {
  const response = await axios.post(
    `${apiUrl()}/cardapio/pedidos/${_id}/itens`,
    { items },
    client("cardapio/pedidos/append-items"),
  );
  return normalizeCardapioPedido(response.data);
}

export async function fetchCardapioPedidos(
  search?: string,
  status?: string,
): Promise<CardapioPedido[]> {
  const response = await axios.get(`${apiUrl()}/cardapio/pedidos`, {
    ...client("cardapio/pedidos/find"),
    params: {
      page: 1,
      amount: 200,
      ...(String(search || "").trim() ? { search } : {}),
      ...(status ? { status } : {}),
    },
  });
  const pedidos = response.data?.pedidos || [];
  return pedidos.map(normalizeCardapioPedido);
}

/** Atualiza status de um ou mais pedidos ("Em aberto" | "Pago"). */
export async function updateCardapioPedidosStatus(
  ids: number[],
  status: string,
): Promise<{ updated: number }> {
  const response = await axios.patch(
    `${apiUrl()}/cardapio/pedidos/status`,
    { ids, status },
    client("cardapio/pedidos/update-status"),
  );
  return response.data;
}

export interface ClienteAutocomplete {
  cliente_id: number;
  nome: string;
  cpf: string | null;
  pulseira_conteudo: string | null;
  pulseira_mestre: string | null;
}

export function clienteOptionLabel(c: ClienteAutocomplete): string {
  const parts = [c.nome];
  if (c.cpf) parts.push(`CPF ${c.cpf}`);
  if (c.pulseira_conteudo) parts.push(`Pulseira ${c.pulseira_conteudo}`);
  else if (c.pulseira_mestre) parts.push(`Pulseira ${c.pulseira_mestre}`);
  return parts.filter(Boolean).join(" · ");
}

export function clienteWristbandKey(c: ClienteAutocomplete): string {
  return (
    String(c.pulseira_conteudo || "").trim() ||
    String(c.pulseira_mestre || "").trim() ||
    String(c.cpf || "").trim() ||
    String(c.cliente_id)
  );
}

export async function searchClientesAutocomplete(
  q: string,
  limit = 20,
): Promise<ClienteAutocomplete[]> {
  const term = String(q || "").trim();
  if (term.length < 3) return [];
  const response = await axios.get(`${apiUrl()}/clientes/autocomplete`, {
    ...client("clientes/autocomplete"),
    params: { q: term, limit },
  });
  const list = response.data?.clientes || response.data || [];
  return (Array.isArray(list) ? list : []).map((row: Record<string, unknown>) => ({
    cliente_id: Number(row.cliente_id ?? 0),
    nome: String(row.nome ?? ""),
    cpf: row.cpf != null ? String(row.cpf) : null,
    pulseira_conteudo:
      row.pulseira_conteudo != null ? String(row.pulseira_conteudo) : null,
    pulseira_mestre:
      row.pulseira_mestre != null ? String(row.pulseira_mestre) : null,
  }));
}

export interface ClienteDetalhe {
  cliente_id: number;
  nome: string;
  sobrenome: string;
  cpf: string | null;
  imagem_perfil: string | null;
  pulseiras: Array<{
    conteudo: string | null;
    mestre: string | null;
  }>;
}

/** Detalhe do cliente (usuarios + pulseiras). */
export async function fetchClienteById(
  clienteId: number,
): Promise<ClienteDetalhe | null> {
  const id = Number(clienteId || 0);
  if (!id) return null;
  const response = await axios.get(`${apiUrl()}/clientes/${id}`, {
    ...client("clientes/find-by-id"),
  });
  const row = response.data?.cliente || response.data;
  if (!row) return null;
  return {
    cliente_id: Number(row.cliente_id ?? id),
    nome: String(row.nome ?? ""),
    sobrenome: String(row.sobrenome ?? ""),
    cpf: row.cpf != null ? String(row.cpf) : null,
    imagem_perfil:
      row.imagem_perfil != null ? String(row.imagem_perfil) : null,
    pulseiras: Array.isArray(row.pulseiras)
      ? row.pulseiras.map((p: Record<string, unknown>) => ({
          conteudo: p.conteudo != null ? String(p.conteudo) : null,
          mestre: p.mestre != null ? String(p.mestre) : null,
        }))
      : [],
  };
}

export async function fetchCardapioPedidoById(
  _id: number,
): Promise<CardapioPedido> {
  const response = await axios.get(
    `${apiUrl()}/cardapio/pedidos/${_id}`,
    client("cardapio/pedidos/find-by-id"),
  );
  return normalizeCardapioPedido(response.data);
}

export interface SicrediPixPaymentPayload {
  id: {
    usuario: string;
    companhia: string;
    setor: string;
    cliente: string;
    pulseira: string;
    item: string;
  };
  pagamento: {
    valor: number;
    multa: number;
    juros: number;
    vencimento: string;
  };
  pix: {
    id_dispositivo: string;
    timeout: number;
    limite: number;
    parcelamento: boolean;
  };
  dispositivo: {
    nome: string;
    modelo: string;
    serial: string;
    versao: string;
    bateria: string;
    ip: string;
    status: string;
    ultimo_acesso: string;
  };
  local: {
    cidade: string;
    endereco: string;
  };
  caixa_id?: number;
  caixa_sessao_id?: number;
  order_id?: number;
  /** AAC-113: pedidos do cardápio que o PIX paga; o servidor marca como "Pago" na baixa. */
  cardapio_pedido_ids?: number[];
  /** AAC-113: funcionário da venda (transação do Caixa Financeiro registrada pelo servidor). */
  funcionario_id?: number;
}

export interface SicrediPixPaymentResponse {
  txid?: string;
  pix_copia_e_cola?: string;
  uuid?: string;
  payment_id?: number;
  resposta_sicredi?: {
    status: string;
    txid?: string;
    pixCopiaECola?: string;
    calendario?: { expiracao: number; criacao: string };
    [key: string]: unknown;
  };
}

export async function createSicrediPixPayment(
  data: SicrediPixPaymentPayload,
): Promise<SicrediPixPaymentResponse> {
  const response = await axios.post<SicrediPixPaymentResponse>(
    `${apiUrl()}/payments/sicredi-pix`,
    data,
    client("payments/sicredi-pix/create"),
  );
  return response.data;
}

export async function fetchSicrediPixStatus(txid: string) {
  const response = await axios.get(`${apiUrl()}/payments/sicredi-pix/${txid}/status`, {
    headers: headers("payments/sicredi-pix/status"),
  });
  return response.data;
}

export async function fetchCardapioOperationalReport(
  filters: { start_date?: string; end_date?: string } = {},
): Promise<CardapioOperationalReport> {
  const response = await axios.get<CardapioOperationalReport>(
    `${apiUrl()}/cardapio/relatorios/operacional`,
    {
      ...client("cardapio/relatorios/operacional"),
      params: {
        ...(filters.start_date ? { start_date: filters.start_date } : {}),
        ...(filters.end_date ? { end_date: filters.end_date } : {}),
      },
    },
  );

  const data = response.data || ({} as CardapioOperationalReport);
  return {
    summary: {
      orders: Number(data.summary?.orders) || 0,
      revenue: Number(data.summary?.revenue) || 0,
      commission: Number(data.summary?.commission) || 0,
    },
    by_product: Array.isArray(data.by_product)
      ? data.by_product.map((row) => ({
          _id_cardapio_item: Number(row._id_cardapio_item) || 0,
          item_name: String(row.item_name ?? ""),
          quantity: Number(row.quantity) || 0,
          revenue: Number(row.revenue) || 0,
        }))
      : [],
    by_seller: Array.isArray(data.by_seller)
      ? data.by_seller.map((row) => ({
          seller_name: String(row.seller_name ?? ""),
          orders: Number(row.orders) || 0,
          revenue: Number(row.revenue) || 0,
          commission: Number(row.commission) || 0,
        }))
      : [],
  };
}
